import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PawPrint, ArrowLeft, Mail } from "lucide-react";
import { Logo } from "@/components/app/Logo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-background">
      <Card className="w-full max-w-md border-border shadow-none rounded-lg" data-testid="forgot-password-card">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-8">
            <Logo size={64} textClassName="text-2xl" />
          </div>

          {sent ? (
            <div data-testid="forgot-sent">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Check your email</p>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-4">
                Reset link on the way
              </h1>
              <Card className="border-border shadow-none rounded-lg bg-secondary mb-6">
                <CardContent className="p-5 flex items-start gap-3">
                  <Mail size={18} className="text-primary shrink-0 mt-0.5" />
                  <p className="text-sm">
                    If <strong>{email}</strong> matches an account, we just emailed a link to reset your password.
                    It expires in 30 minutes.
                  </p>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground mb-6">
                Didn&apos;t get it? Check spam, or wait a minute and try again. (You can request another link every 60 seconds.)
              </p>
              <Link to="/login" data-testid="back-to-login-link" className="text-sm font-medium underline underline-offset-4 flex items-center gap-1.5">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Forgot password</p>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-3">
                Reset your password
              </h1>
              <p className="text-sm text-muted-foreground mb-8">
                Enter the email you used to sign up and we&apos;ll send you a link to choose a new password.
              </p>

              <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5"
                    required
                  />
                </div>
                {error ? (
                  <div data-testid="forgot-error" className="text-sm text-destructive">{error}</div>
                ) : null}
                <Button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
              </form>

              <p className="text-sm text-muted-foreground mt-6">
                Remembered it?{" "}
                <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="back-to-login-link">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
